
import React, { useState } from 'react';
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { 
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle2, Loader } from "lucide-react";

const formSchema = z.object({
  name: z.string().min(2, {
    message: "Name must be at least 2 characters.",
  }),
  email: z.string().email({
    message: "Please enter a valid email address.",
  }),
  company: z.string().optional(),
  phone: z.string().optional(),
  message: z.string().min(5, {
    message: "Message must be at least 5 characters.",
  }),
});

const ContactForm = () => {
  const { toast } = useToast();
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage];
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      company: "",
      phone: "",
      message: "",
    },
  });

  function onSubmit(values: z.infer<typeof formSchema>) {
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      console.log('Form submitted:', values);
      
      setIsSubmitting(false);
      setIsSuccess(true);
      
      // Show success message
      toast({
        title: t.formSubmitSuccess,
        description: t.formSubmitMessage,
        duration: 5000,
      });
      
      // Reset success state after animation
      setTimeout(() => {
        setIsSuccess(false);
        form.reset();
      }, 3000);
    }, 1500);
  }

  return (
    <section className="py-16 md:py-24 relative overflow-hidden" id="contact">
      {/* Neural network background animation */}
      <div className="absolute inset-0 pointer-events-none neural-pattern opacity-20"></div>
      
      {/* Light streaks */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute h-[300px] w-[2px] bg-gradient-to-b from-transparent via-purple-500/20 to-transparent top-0 left-[30%] animate-[float_15s_ease-in-out_infinite]"></div>
        <div className="absolute h-[200px] w-[2px] bg-gradient-to-b from-transparent via-purple-500/10 to-transparent top-[20%] left-[70%] animate-[float_20s_ease-in-out_infinite_reverse]"></div>
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12 animate-fade-in">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white font-future tracking-wider">
              {t.contactUs}
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto mb-6">
              {t.contactSubtitle}
            </p>
            <div className="w-3 h-3 rounded-full bg-purple-500 mx-auto mt-2 animate-pulse"></div>
          </div>
          
          <div className="glass-card rounded-xl p-6 md:p-10 shadow-lg shadow-purple-500/10 transform transition-all duration-500 hover:shadow-purple-500/20 animate-scale-in">
            {isSuccess ? (
              <div className="flex flex-col items-center justify-center py-16 text-center animate-fade-in">
                <CheckCircle2 className="w-16 h-16 text-green-500 mb-6 animate-scale-in" />
                <h3 className="text-xl md:text-2xl font-bold text-white mb-4">
                  {t.formSubmitSuccess}
                </h3>
                <p className="text-gray-300">
                  {t.formSubmitMessage}
                </p>
              </div>
            ) : (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-200">
                            {t.fullName} *
                          </FormLabel>
                          <FormControl>
                            <Input 
                              {...field} 
                              className="neo-input" 
                              placeholder={t.fullName}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-200">
                            {t.email} *
                          </FormLabel>
                          <FormControl>
                            <Input 
                              {...field} 
                              type="email"
                              className="neo-input" 
                              placeholder="email@example.com"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="company"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-200">
                            {t.company}
                          </FormLabel>
                          <FormControl>
                            <Input 
                              {...field} 
                              className="neo-input" 
                              placeholder={t.company}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-200">
                            {t.phone}
                          </FormLabel>
                          <FormControl>
                            <Input 
                              {...field} 
                              type="tel"
                              className="neo-input" 
                              placeholder="+49 123 456789"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <FormField
                    control={form.control}
                    name="message"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-gray-200">
                          {t.projectDescription} *
                        </FormLabel>
                        <FormControl>
                          <Textarea 
                            {...field} 
                            className="neo-input min-h-[120px]" 
                            placeholder={t.projectDescription}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <div className="text-center pt-4">
                    <div className="flex flex-col items-center">
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="neo-button px-8 py-3 group"
                      >
                        {isSubmitting ? (
                          <div className="flex items-center">
                            <Loader className="w-4 h-4 mr-2 animate-spin" />
                            <span>{t.sending}...</span>
                          </div>
                        ) : (
                          <div className="flex items-center">
                            <span>{t.send}</span>
                            <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                          </div>
                        )}
                      </Button>
                      
                      <div className="mt-6 text-xs text-gray-400 flex items-center justify-center">
                        <svg 
                          viewBox="0 0 24 24" 
                          className="w-4 h-4 mr-1 text-purple-400"
                          fill="none" 
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                        </svg>
                        <span>{t.responseTime}</span>
                      </div>
                    </div>
                  </div>
                </form>
              </Form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
